import { useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Auth } from "./Auth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from '@/utils/logger';

export interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: "signin" | "signup";
  /** Optional contextual message explaining why auth is needed */
  message?: string;
}

export const AuthDialog = ({ isOpen, onClose, defaultTab = "signin", message }: AuthDialogProps) => {
  const { toast } = useToast();


  useEffect(() => {
    if (!isOpen) return;

    const setupAuth = async () => {
      try {
        // Check for existing session first
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (existingSession) {
          // Try to refresh the session if it exists
          const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            if (refreshError.message.includes('refresh_token_not_found')) {
              logger.warn('Invalid refresh token detected, clearing session', { 
                component: 'AuthDialog', 
                action: 'tokenValidation' 
              });
              await supabase.auth.signOut();
            }
            throw refreshError;
          }
          if (session) {
            onClose();
            return;
          }
        }

        // Set up auth state change listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          logger.info('Auth state changed', { 
            component: 'AuthDialog', 
            action: 'authStateChange' 
          }, { event, userId: session?.user?.id });
          if (event === 'SIGNED_IN' && session) {
            toast({
              title: "Success",
              description: "Successfully signed in!",
            });
            onClose();
          } else if (event === 'TOKEN_REFRESHED') {
            logger.debug('Auth token refreshed successfully', { 
              component: 'AuthDialog', 
              action: 'tokenRefresh' 
            });
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error: any) {
        logger.error('Auth setup failed', { 
          component: 'AuthDialog', 
          action: 'authSetup' 
        }, error);
        if (error.message.includes('refresh_token_not_found')) {
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please sign in again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Authentication Error",
            description: "There was a problem with authentication. Please try again.",
            variant: "destructive",
          });
        }
      }
    };

    setupAuth();
  }, [isOpen, onClose, toast]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogTitle className="sr-only">Authentication</DialogTitle>
        {message && (
          <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground">
            {message}
          </div>
        )}
        <Auth defaultTab={defaultTab} />
      </DialogContent>
    </Dialog>
  );
};
